import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

const BASE = '/pricing-rules/deposit-payment-terms';

/**
 * Map API rule to frontend format
 */
function mapRuleFromApi(rule) {
  if (!rule) return null;
  const depositType = rule.deposit_type === 'percentage' ? 'Percentage' : 'Fixed';
  const depositValueStr = rule.deposit_value || '';
  const rawValue = depositType === 'Percentage'
    ? parseFloat(String(depositValueStr).replace('%', '')) || 0
    : parseFloat(String(depositValueStr).replace(/[€,]/g, '')) || 0;

  return {
    id: rule.id,
    name: rule.rule_name,
    rule_description: rule.rule_description,
    depositType,
    depositValue: rawValue,
    depositValueDisplay: depositValueStr,
    productDependent: rule.product_dependent ?? false,
    startDate: rule.start_date || '',
    endDate: rule.end_date || '',
    status: rule.status ?? true,
    frontend: rule.frontend ?? true,
    backend: rule.backend ?? true,
    createdAt: rule.createdAt,
    updatedAt: rule.updatedAt,
  };
}

/**
 * Map frontend form to API payload
 */
function mapRuleToApi(form, isCreate = false) {
  const deposit_type = form.depositType === 'Percentage' ? 'percentage' : 'fixed';
  const payload = {
    rule_name: form.name?.trim() || form.rule_name,
    rule_description: form.rule_description || '',
    deposit_type,
    deposit_value: parseFloat(form.depositValue) || 0,
    start_date: form.startDate || form.start_date,
    end_date: form.endDate || form.end_date || null,
    product_dependent: form.productDependent ?? false,
    status: form.status ?? true,
  };
  if (!isCreate) {
    if (form.frontend !== undefined) payload.frontend = form.frontend;
    if (form.backend !== undefined) payload.backend = form.backend;
  }
  return payload;
}

/**
 * GET Deposit Rules (with optional filters: q, status, limit, offset)
 */
export function useDepositRules(params = {}) {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async (queryParams = {}) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(BASE, {
        params: { ...params, ...queryParams },
      });
      const list = res?.data ?? [];
      const mapped = list.map(mapRuleFromApi);
      setData(mapped);
      setSummary(res?.summary ?? null);
      return mapped;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load deposit rules';
      setError(msg);
      setData([]);
      setSummary(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [params.q, params.status, params.limit, params.offset]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { data, summary, loading, error, refetch: fetchList };
}

/**
 * GET Deposit Rules Count
 */
export function useDepositRulesCount() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCount = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${BASE}/count`);
      setData(res?.data ?? null);
      return res?.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load deposit count';
      setError(msg);
      setData(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return { data: data, loading, error, refetch: fetchCount };
}

/**
 * CREATE Deposit Rule
 */
export function useCreateDepositRule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (form) => {
    setLoading(true);
    setError(null);
    try {
      const payload = mapRuleToApi(form, true);
      const { data: res } = await axiosInstance.post(BASE, payload);
      return { success: true, data: mapRuleFromApi(res?.data ?? res) };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Failed to create deposit rule';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * UPDATE Deposit Rule
 */
export function useUpdateDepositRule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, form) => {
    if (!id) throw new Error('Rule id is required');
    setLoading(true);
    setError(null);
    try {
      const payload = mapRuleToApi(form, false);
      await axiosInstance.put(`${BASE}/${id}`, payload);
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Failed to update deposit rule';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * UPDATE Deposit Rule Status (toggle)
 */
export function useUpdateDepositRuleStatus() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, status) => {
    if (!id) throw new Error('Rule id is required');
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.put(`${BASE}/${id}`, { status });
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Failed to update status';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * DELETE Deposit Rule
 */
export function useDeleteDepositRule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    if (!id) throw new Error('Rule id is required');
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.delete(`${BASE}/${id}`);
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Failed to delete deposit rule';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * Stub: GET Deposit Exceptions (no backend API yet)
 */
export function useDepositExceptions() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData([]);
      return [];
    } catch (err) {
      setData([]);
      setError(err.message || 'Failed to load exceptions');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { data, loading, error, refetch: fetchList };
}
